export const roundTo = (value, step = 2.5) => Math.round(value / step) * step;

export const FORMULAS = {
  epley: { label: 'Epley', fn: (w, r) => w * (1 + r / 30) },
  brzycki: { label: 'Brzycki', fn: (w, r) => (r >= 37 ? NaN : (w * 36) / (37 - r)) },
  lombardi: { label: 'Lombardi', fn: (w, r) => w * Math.pow(r, 0.1) },
  lander: { label: 'Lander', fn: (w, r) => (r >= 100 ? NaN : (100 * w) / (101.3 - 2.67123 * r)) },
};

export function estimate1RM(weight, reps, formula = 'average') {
  if (!(weight > 0) || !(reps >= 1)) return null;
  if (reps === 1) return weight;
  if (formula !== 'average') {
    const v = FORMULAS[formula].fn(weight, reps);
    return Number.isFinite(v) ? v : null;
  }
  const values = Object.values(FORMULAS).map((f) => f.fn(weight, reps)).filter(Number.isFinite);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

export const PERCENT_STEPS = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50];

// Répétitions approximatives réalisables à un pourcentage du 1RM
const REPS_AT = { 100: 1, 95: 2, 90: 4, 85: 6, 80: 8, 75: 10, 70: 12, 65: 15, 60: 18, 55: 22, 50: 25 };

export function percentTable(oneRm, step = 2.5) {
  if (!(oneRm > 0)) return [];
  return PERCENT_STEPS.map((pct) => ({
    pct,
    weight: roundTo((oneRm * pct) / 100, step),
    reps: REPS_AT[pct],
  }));
}

const WARMUP_SCHEME = [
  { pct: 0.4, reps: 8 },
  { pct: 0.6, reps: 5 },
  { pct: 0.75, reps: 3 },
  { pct: 0.9, reps: 1 },
];

// Échauffement pyramidal vers une charge de travail
export function warmupSets(workWeight, { bar = 20, step = 2.5 } = {}) {
  if (!(workWeight > 0)) return [];
  const sets = [];
  if (workWeight > bar) sets.push({ weight: bar, reps: 10, label: 'Barre à vide' });
  WARMUP_SCHEME.forEach(({ pct, reps }) => {
    const weight = roundTo(workWeight * pct, step);
    const last = sets[sets.length - 1];
    if (weight > bar && weight < workWeight && (!last || weight > last.weight)) {
      sets.push({ weight, reps, label: `${Math.round(pct * 100)} %` });
    }
  });
  return sets;
}

export const DEFAULT_PLATES = [25, 20, 15, 10, 5, 2.5, 1.25];

// Disques à charger de chaque côté de la barre
export function platesPerSide(target, bar = 20, plates = DEFAULT_PLATES) {
  let remaining = (target - bar) / 2;
  if (!(remaining > 0)) return { plates: [], remainder: 0 };
  const result = [];
  plates.forEach((p) => {
    while (remaining + 1e-9 >= p) {
      result.push(p);
      remaining -= p;
    }
  });
  return { plates: result, remainder: Math.round(remaining * 2 * 100) / 100 };
}

// Propose la charge de la prochaine séance : +incrément si toutes les séries ont atteint le haut de la fourchette
export function suggestProgression(lastSets, { repsMax, isTimed = false, step = 2.5 } = {}) {
  if (isTimed || !(repsMax > 0)) return null;
  const done = (lastSets || []).filter((s) => s.weight > 0 && s.reps > 0);
  if (!done.length) return null;
  const weight = Math.max(...done.map((s) => s.weight));
  const atWeight = done.filter((s) => s.weight === weight);
  if (atWeight.every((s) => s.reps >= repsMax)) {
    return { weight: weight + step, reps: null, reason: `Toutes les séries à ${repsMax} reps : monte la charge` };
  }
  return { weight, reps: null, reason: `Vise ${repsMax} reps sur chaque série avant d'augmenter` };
}
